import { SxProps, Theme } from "@mui/material";
import { GenericAbortSignal } from "axios";
import { MprcAutoCompleteHierarchical } from "components/MprcAutoCompleteHierarchical";
import { AutocompleteHierarchicalBaseApplyEnter } from "components/MprcAutoCompleteHierarchical/AutoCompleteHierarchical";
import { AutoCompleteHierarchicalModel, AutoCompleteHierarchicalSuggestions, AutoCompleteSelectedMode, UnitAutoCompleteHierarchicalModel } from "components/MprcAutoCompleteHierarchical/AutoCompleteHierarchicalModel";
import { UserSelectionEntityType, UserSelectionEntry } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import React from "react";

////////////////////////////////////////////////////////////////////////////

export interface IUnitAutoCompleteProps {
    label?: string;
    selectedItems: UnitAutoCompleteHierarchicalModel[];
    multiSelect: boolean;
    required?: boolean;
    disabled?: boolean;
    margin?: MuiMargin;
    error?: boolean;
    helperText?: React.ReactNode;
    selectedMode: AutoCompleteSelectedMode;
    showDescendantOption?: boolean;
    autoFocus?: boolean;
    placeholder?: string;
    inputRef?: React.Ref<any>;
    onChange: (item: UnitAutoCompleteHierarchicalModel[]) => void;
    onError: (error: any) => void;
    // Ao pressionar ESC quando nenhum item esta selecionado
    onCancel?: () => void;
    applyEnter?: AutocompleteHierarchicalBaseApplyEnter;
    sx?: SxProps<Theme>;
}

export interface IUnitAutoCompleteHierarchical extends IUnitAutoCompleteProps {
    showUnitTypeName: boolean;
    onGetSuggestions: (parentId: string | null, trySelectionHistory: boolean, searchText: string, pageNumber: number, pageSize: number, signal: GenericAbortSignal | undefined) => Promise<AutoCompleteHierarchicalSuggestions<AutoCompleteHierarchicalModel>>;
}

////////////////////////////////////////////////////////////////////////////

/**
 * Auto complete hierarquico de unidades administrativa
 * @param props
 */
export const UnitAutoCompleteHierarchicalComponent: React.FC<IUnitAutoCompleteHierarchical> = (props) => {

    const {
        disabled = false, showUnitTypeName = false, required = false, selectedMode = "free",
        label, margin, multiSelect, placeholder, inputRef, selectedItems, error, helperText, autoFocus, showDescendantOption, applyEnter, sx,
        onChange, onGetSuggestions, onCancel
    } = props;
    const api = useCoreApi();
    const session = useSession();

    /// Carrega a lista de sugestões
    const getSuggestions = (parentId: string | null, trySelectionHistory: boolean, searchText: string, pageNumber: number, pageSize: number, signal: GenericAbortSignal | undefined): Promise<AutoCompleteHierarchicalSuggestions<AutoCompleteHierarchicalModel>> => {
        searchText = searchText.toLowerCase();
        return onGetSuggestions(parentId, trySelectionHistory, searchText, pageNumber, pageSize, signal);
    };

    ///
    const handleDisplayLabel = (option: UnitAutoCompleteHierarchicalModel): string => {
        return showUnitTypeName ? `${option.unitTypeName}: ${option.label}` : option.label;
    };

    ///
    const handleDisplayTooltip = (item: UnitAutoCompleteHierarchicalModel): string => {

        if (!showUnitTypeName)
            return item.tree && item.tree.length > 0 ? `${item.tree.map(x => x.parentLabel).join("\\")}\\${item.label}` : item.label;

        const tree = item.tree?.filter((el, index) => index > 0) ?? [];
        return tree && tree.length > 0 ? `${tree.map(x => x.parentLabel).join("\\")}\\${item.label}` : item.label;
    };

    //Salva histórico ao selecionar
    const handleSelectItem = (item: UnitAutoCompleteHierarchicalModel) => {
        var payload = {
            userId: session.user.userId,
            entries: [{
                entityType: UserSelectionEntityType.Unit,
                entityId: item.key,
                otherInfo: item.includeDescendants ? 'true' : 'false'
            } as UserSelectionEntry]
        };

        api.v1.user.saveSelectionHistory(payload);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <MprcAutoCompleteHierarchical
            label={label ?? ""}
            margin={margin}
            multiSelect={multiSelect}
            disabled={disabled}
            selectedMode={selectedMode}
            showDescendantOption={showDescendantOption}
            selectedItems={selectedItems}
            placeholder={placeholder}
            onGetSuggestions={getSuggestions}
            onCancel={onCancel}
            onChange={(items) => onChange(items as UnitAutoCompleteHierarchicalModel[])}
            onDisplayLabel={(option) => handleDisplayLabel(option as UnitAutoCompleteHierarchicalModel)}
            onDisplayTooltip={(item) => handleDisplayTooltip(item as UnitAutoCompleteHierarchicalModel)}
            onSelectItem={(item) => handleSelectItem(item as UnitAutoCompleteHierarchicalModel)}
            applyEnter={applyEnter}
            error={error}
            helperText={helperText}
            required={required}
            autoFocus={autoFocus}
            sx={sx}
            inputRef={inputRef}
        />
    );
};
